"use client";

import { useEffect, useMemo, useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { SeriesScoreEditor } from "./series-score-editor";

const isRecord = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === "object" && !Array.isArray(value);

const parseOptionalInt = (value: unknown) => {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value !== "string") return null;
  if (value.trim() === "") return null;
  const parsed = Number.parseInt(value, 10);
  return Number.isFinite(parsed) ? parsed : null;
};

export type SnookerScoreEditorProps = {
  initialData?: Record<string, unknown> | null;
  onChange: (data: Record<string, unknown>) => void;
  maxDetailCount?: number;
};

export function SnookerScoreEditor({
  initialData,
  onChange,
  maxDetailCount = 25,
}: SnookerScoreEditorProps) {
  const [seriesData, setSeriesData] = useState<Record<string, unknown>>(() => {
    const matchScore = isRecord(initialData?.matchScore)
      ? (initialData?.matchScore as Record<string, unknown>)
      : null;
    const yourFrames = parseOptionalInt(matchScore?.yourFrames) ?? 0;
    const opponentFrames = parseOptionalInt(matchScore?.opponentFrames) ?? 0;

    const rawFrames = initialData?.frames;
    const frames = Array.isArray(rawFrames)
      ? (rawFrames as unknown[])
          .map((row) => {
            if (!isRecord(row)) return null;
            const your = parseOptionalInt(
              row.yourScore ?? row.team1Score ?? row.team1,
            );
            const opponent = parseOptionalInt(
              row.opponentScore ?? row.team2Score ?? row.team2,
            );
            if (your == null || opponent == null) return null;
            return { yourScore: your, opponentScore: opponent };
          })
          .filter(
            (row): row is { yourScore: number; opponentScore: number } =>
              row !== null,
          )
      : [];

    return { matchScore: { yourFrames, opponentFrames }, frames };
  });

  const [highestBreak, setHighestBreak] = useState(
    parseOptionalInt(initialData?.highestBreak) ?? 0,
  );

  useEffect(() => {
    onChange({
      ...seriesData,
      ...(highestBreak > 0 ? { highestBreak } : {}),
    });
  }, [seriesData, highestBreak, onChange]);

  const breakLabel = useMemo(() => {
    if (highestBreak < 50) return null;
    if (highestBreak >= 147) return "🏆 Maximum!";
    if (highestBreak >= 100) return "🎱 Century!";
    return "🎱 Half-century!";
  }, [highestBreak]);

  return (
    <div className="space-y-3">
      <SeriesScoreEditor
        matchScoreLabel="Match Score (Frames Won)"
        detailsLabel="Frame Details (Optional)"
        detailsHint="Enter points per frame (or 1–0 if you only tracked the winner). Frames won are auto-calculated."
        unitSingularLabel="Frame"
        matchScoreKeys={{ your: "yourFrames", opponent: "opponentFrames" }}
        detailsKey="frames"
        initialData={initialData}
        maxDetailCount={maxDetailCount}
        detailMaxScore={200}
        onChange={setSeriesData}
      />

      <div className="space-y-2">
        <Label className="text-text-primary">Highest Break (optional)</Label>
        <Input
          type="number"
          value={highestBreak || ""}
          onChange={(e) =>
            setHighestBreak(Math.min(147, Number(e.target.value) || 0))
          }
          className="w-24"
          placeholder="0"
          max={147}
        />
        {breakLabel ? (
          <span className="text-sm text-brand">{breakLabel}</span>
        ) : null}
      </div>
    </div>
  );
}
